import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';
import { formatCurrency } from '../utils/formatters';
import { closePosition, executeOrder } from '../services/trading'; // Import trading service
import { showToast } from './ToastNotification';

const StockChart = ({
  stockId,
  ticker,
  positions = [],
  stockHistory,
  isProMode,
  onPositionUpdate, // Expects (result, actionDetails)
  userId, // New prop
  compact = false // Compact mode for inline display
}) => {
  const [chartData, setChartData] = useState([]);
  const [timeRange, setTimeRange] = useState('1h');
  const [interval, setInterval] = useState('1m');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionStockId, setActionStockId] = useState(null);
  const [orderAmount, setOrderAmount] = useState('1');
  
  // Process history data when it changes or time range/interval changes
  useEffect(() => {
    if (!stockHistory || !Array.isArray(stockHistory) || stockHistory.length === 0) {
      setChartData([]);
      return;
    }
    
    // Format the data for the chart
    let formattedData = stockHistory.map(point => ({
      time: new Date(point.timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }),
      fullTimestamp: new Date(point.timestamp),
      price: parseFloat(point.price)
    }));
    // Sort by timestamp (oldest first)
    formattedData.sort((a, b) => a.fullTimestamp - b.fullTimestamp);
    
    // Filter data based on time range
    // Use the latest data point as reference instead of current time
    // This handles cases where data timestamps might be in the future or past
    const latestDataTime = formattedData.length > 0 ?
      new Date(Math.max(...formattedData.map(d => d.fullTimestamp.getTime()))) :
      new Date();

    let cutoffTime;

    switch (timeRange) {
      case '5m':
        cutoffTime = new Date(latestDataTime.getTime() - 5 * 60 * 1000);
        break;
      case '15m':
        cutoffTime = new Date(latestDataTime.getTime() - 15 * 60 * 1000);
        break;
      case '30m':
        cutoffTime = new Date(latestDataTime.getTime() - 30 * 60 * 1000);
        break;
      case '1h':
        cutoffTime = new Date(latestDataTime.getTime() - 60 * 60 * 1000);
        break;
      case '1d':
        cutoffTime = new Date(latestDataTime.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '1w':
        cutoffTime = new Date(latestDataTime.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      default:
        cutoffTime = new Date(latestDataTime.getTime() - 60 * 60 * 1000); // Default to 1 hour
    }

    // Filter by time range - keep points after cutoff
    formattedData = formattedData.filter(point => point.fullTimestamp >= cutoffTime);

    // If no data remains after filtering, just show all available data
    if (formattedData.length === 0 && stockHistory.length > 0) {
      formattedData = stockHistory.map(point => ({
        time: new Date(point.timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        }),
        fullTimestamp: new Date(point.timestamp),
        price: parseFloat(point.price)
      }));
      formattedData.sort((a, b) => a.fullTimestamp - b.fullTimestamp);
    }
    
    // Apply interval filtering for timeframes longer than 30 minutes
    if (timeRange !== '5m' && timeRange !== '15m' && timeRange !== '30m') {
      // Determine appropriate intervals based on the selected interval and timeRange
      const skipCount = getIntervalSkipCount(interval, timeRange);
      
      if (skipCount > 1) {
        formattedData = formattedData.filter((_, index) => index % skipCount === 0);
      }
    }
    
    // Update the chart data
    setChartData(formattedData);
  }, [stockHistory, timeRange, interval]);
  
  // Helper function to determine how many data points to skip
  const getIntervalSkipCount = (selectedInterval, selectedTimeRange) => {
    if (selectedTimeRange === '5m' || selectedTimeRange === '15m' || selectedTimeRange === '30m') {
      return 1; // No filtering for short timeframes
    }
    
    switch (selectedInterval) {
      case '1m':
        return 1; // Show all data points
      case '5m':
        return 5; // Show every 5th data point
      case '15m':
        return 15; // Show every 15th data point
      case '30m':
        return 30; // Show every 30th data point
      case '1h':
        return 60; // Show hourly data points
      default:
        return 1;
    }
  };
  
  // Filter positions for this specific stock
  const stockPositions = positions.filter(position => position.stockId === stockId);
  
  // Find min and max prices for better Y-axis scaling
  const prices = chartData.map(point => point.price);
  const minPrice = prices.length > 0 ? Math.min(...prices) * 0.995 : 0; // 0.5% padding
  const maxPrice = prices.length > 0 ? Math.max(...prices) * 1.005 : 0; // 0.5% padding
  
  // Handle time range changes
  const handleRangeChange = (newRange) => {
    setTimeRange(newRange);
    
    // Reset interval for shorter timeframes
    if (newRange === '5m' || newRange === '15m' || newRange === '30m') {
      setInterval('1m');
    } else if (newRange === '1h') {
      setInterval('1m');
    } else if (newRange === '1d') {
      setInterval('5m');
    } else if (newRange === '1w') {
      setInterval('1h');
    }
  };
  
  // Handle interval changes
  const handleIntervalChange = (newInterval) => {
    setInterval(newInterval);
  };
  
  // Determine if interval selector should be shown based on timeRange
  const showIntervalSelector = timeRange === '1h' || timeRange === '1d' || timeRange === '1w';
  
  // Render interval selector
  const renderIntervalSelector = () => {
    if (!showIntervalSelector) return null;
    
    // Determine available intervals based on timeRange
    let availableIntervals = [];
    
    if (timeRange === '1h') {
      availableIntervals = ['1m', '5m'];
    } else if (timeRange === '1d') {
      availableIntervals = ['5m', '15m', '30m', '1h'];
    } else if (timeRange === '1w') {
      availableIntervals = ['1h', '4h', '1d'];
    }

    return null;
    
    // return (
      // <div className="interval-selector">
      //   <span>Interval:</span>
      //   {availableIntervals.map(int => (
      //     <button 
      //       key={int}
      //       className={interval === int ? 'active' : ''} 
      //       onClick={() => handleIntervalChange(int)}
      //     >
      //       {int.toUpperCase()}
      //     </button>
      //   ))}
      // </div>
    // );
  };

  // New function to handle trade execution (LONG/SHORT)
  const handleTrade = async (type) => { // type is 'LONG' or 'SHORT'
    if (!userId) {
      console.error('No user ID provided for trade execution');
      if (onPositionUpdate) onPositionUpdate({ error: 'User ID not provided' }, { actionType: `OPEN_${type}`, stockTicker: ticker });
      return;
    }
    
    const currentChartPrice = chartData.length > 0 ? chartData[chartData.length - 1].price : null;
    if (!currentChartPrice) {
      if (onPositionUpdate) onPositionUpdate({ error: 'Cannot determine current price' }, { actionType: `OPEN_${type}`, stockTicker: ticker, tradeAmount: orderAmount });
      return;
    }

    setActionLoading(true);
    setActionStockId(stockId);
    try {
      const result = await executeOrder(userId, stockId, type, Math.max(1, parseInt(orderAmount) || 1));
      if (onPositionUpdate) {
        console.log('[StockChart handleTrade] Calling onPositionUpdate with success:', result); // Log
        onPositionUpdate(
          result,
          {
            actionType: `OPEN_${type}`,
            stockTicker: ticker,
            tradeAmount: orderAmount,
            entryPrice: result.position?.entryPrice || currentChartPrice
          }
        );
      }

      // Show toast notification with clear title and details
      const title = `${type} POSITION`;
      const toastMessage = `${orderAmount}x ${ticker}`;
      const details = `Entry: ${formatCurrency(result.position?.entryPrice || currentChartPrice)}`;
      showToast(toastMessage, type.toLowerCase(), 4000, details, title);
    } catch (error) {
      console.error('Trade failed:', error);
      if (onPositionUpdate) {
        console.log('[StockChart handleTrade] Calling onPositionUpdate with error:', error.message); // Log
        onPositionUpdate(
          { error: error.message || 'Unknown error' }, 
          { 
            actionType: `OPEN_${type}`, 
            stockTicker: ticker, 
            tradeAmount: orderAmount,
            entryPrice: currentChartPrice
          }
        );
      }
    } finally {
      setActionLoading(false);
      setActionStockId(null);
    }
  };

  // New function to handle closing a position
  const handleClosePosition = async (positionId) => {
    if (!userId) {
      console.error('No user ID provided for closing position');
      if (onPositionUpdate) onPositionUpdate({ error: 'User ID not provided' }, { actionType: 'CLOSE', stockTicker: ticker });
      return;
    }
    
    const positionToClose = stockPositions.find(p => p.id === positionId);
    if (!positionToClose) {
      if (onPositionUpdate) onPositionUpdate({ error: 'Position not found to close in chart.' }, { actionType: 'CLOSE', stockTicker: ticker });
      return;
    }

    setActionLoading(true);
    setActionStockId(stockId);
    try {
      const result = await closePosition(userId, positionId);
      if (onPositionUpdate) {
        console.log('[StockChart handleClosePosition] Calling onPositionUpdate with success:', result); // Log
        onPositionUpdate(result, { actionType: 'CLOSE', stockTicker: ticker });
      }
    } catch (error) {
      console.error('Close position failed:', error);
      if (onPositionUpdate) {
        console.log('[StockChart handleClosePosition] Calling onPositionUpdate with error:', error.message); // Log
        onPositionUpdate({ error: error.message || 'Unknown error' }, { actionType: 'CLOSE', stockTicker: positionToClose?.ticker || ticker });
      }
    } finally {
      setActionLoading(false);
      setActionStockId(null);
    }
  };
  
  // Get current price from chart data
  const currentPrice = chartData.length > 0 ? 
    chartData[chartData.length - 1].price : 
    null;
  
  return (
    <div className={`stock-chart-container ${compact ? 'compact' : ''}`}>
      {!compact && (
        <div className="chart-header">
          <h3>{ticker} Price History</h3>
          <div className="time-controls">
            <div className="time-range-selector">
              <button
                className={timeRange === '5m' ? 'active' : ''}
                onClick={() => handleRangeChange('5m')}
              >
                5M
              </button>
              <button
                className={timeRange === '15m' ? 'active' : ''}
                onClick={() => handleRangeChange('15m')}
              >
                15M
              </button>
              <button
                className={timeRange === '30m' ? 'active' : ''}
                onClick={() => handleRangeChange('30m')}
              >
                30M
              </button>
              <button
                className={timeRange === '1h' ? 'active' : ''}
                onClick={() => handleRangeChange('1h')}
              >
                1H
              </button>
              <button
                className={timeRange === '1d' ? 'active' : ''}
                onClick={() => handleRangeChange('1d')}
              >
                1D
              </button>
              <button
                className={timeRange === '1w' ? 'active' : ''}
                onClick={() => handleRangeChange('1w')}
              >
                1W
              </button>
            </div>

            {/* Render interval selector if applicable */}
            {renderIntervalSelector()}
          </div>
        </div>
      )}

      {chartData.length > 0 ? (
        <div className="chart-area">
          <ResponsiveContainer width="100%" height={compact ? 150 : 300}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="time" 
                tickCount={5} 
                minTickGap={30}
              />
              <YAxis 
                domain={[minPrice, maxPrice]} 
                tickFormatter={value => formatCurrency(value)}
              />
              <Tooltip 
                formatter={value => [formatCurrency(value), 'Price']}
                labelFormatter={label => `Time: ${label}`}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="price" 
                stroke="#8884d8" 
                dot={false} 
                activeDot={{ r: 8 }} 
                name={`${ticker} Price`}
              />
              
              {/* Add reference lines for user's positions */}
              {stockPositions.map((position, index) => (
                <ReferenceLine 
                  key={position.id || index} 
                  y={position.entryPrice} 
                  stroke={position.type === 'LONG' ? '#4caf50' : '#f44336'} 
                  strokeDasharray="3 3"
                  label={{ 
                    value: `${position.type} @ ${formatCurrency(position.entryPrice)}`, 
                    fill: position.type === 'LONG' ? '#4caf50' : '#f44336',
                    position: 'insideBottomRight'
                  }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="no-chart-data">
          <p>No price history available for {ticker}</p>
        </div>
      )}
      
      {/* Trading actions for StockChart */}
      {userId && (
        <div className="chart-trading-actions">
          <div className="order-amount-control">
            <label htmlFor="chart-order-amount">
              {isProMode ? 'QUANTITY:' : 'Amount:'}
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              id="chart-order-amount"
              value={orderAmount}
              onChange={(e) => setOrderAmount(e.target.value.replace(/[^0-9]/g, ''))}
              onBlur={() => setOrderAmount(prev => String(Math.max(1, Math.min(100, parseInt(prev) || 1))))}
            />
          </div>
          
          <div className="chart-trade-buttons">
            <button
              className="trade-btn long"
              onClick={() => handleTrade('LONG')}
              disabled={actionLoading || !currentPrice}
            >
              {actionLoading && actionStockId === stockId ? 'Processing...' : `${isProMode ? 'LONG' : 'Long'} (${orderAmount})`}
            </button>
            <button
              className="trade-btn short"
              onClick={() => handleTrade('SHORT')}
              disabled={actionLoading || !currentPrice}
            >
              {actionLoading && actionStockId === stockId ? 'Processing...' : `${isProMode ? 'SHORT' : 'Short'} (${orderAmount})`}
            </button>
          </div>
        </div>
      )}
      
      {/* Statistics Section */}
      {/* {chartData.length > 0 && (
        <div className="price-statistics">
          <div className="stat-box">
            <span className="stat-label">Open</span>
            <span className="stat-value">{formatCurrency(chartData[0]?.price || 0)}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">High</span>
            <span className="stat-value">{formatCurrency(maxPrice || 0)}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">Low</span>
            <span className="stat-value">{formatCurrency(minPrice || 0)}</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">Current</span>
            <span className="stat-value">{formatCurrency(chartData[chartData.length - 1]?.price || 0)}</span>
          </div>
          <div className="stat-box timeframe">
            <span className="timeframe-label">{timeRange.toUpperCase()} / {interval.toUpperCase()}</span>
          </div>
        </div>
      )} */}
      
      {stockPositions.length > 0 && (
        <div className="position-info">
          <h4>Your Positions</h4>
          {stockPositions.map((position, index) => (
            <div 
              key={position.id || index}
              className={`position-card mini ${position.type.toLowerCase()}`}
            >
              <div className="position-header">
                <span className="type">{position.type}</span>
                <span className="shares">{position.shares} {position.shares === 1 ? 'share' : 'shares'}</span>
              </div>
              <div className="position-details">
                <div className="detail">
                  <span className="label">Entry:</span>
                  <span>{formatCurrency(position.entryPrice)}</span>
                </div>
                <div className="detail">
                  <span className="label">Current:</span>
                  <span>{formatCurrency(position.currentPrice || chartData[chartData.length - 1]?.price || 0)}</span>
                </div>
                <div className="detail">
                  <span className="label">P/L:</span>
                  <span className={position.profit >= 0 ? 'positive' : 'negative'}>
                    {position.profit >= 0 ? '+' : ''}
                    {formatCurrency(position.profit)}
                  </span>
                </div>
              </div>
              
              {/* Add close position button */}
              {userId && (
                <div className="position-actions">
                  <button
                    className="close-position-btn"
                    onClick={() => handleClosePosition(position.id)}
                    disabled={actionLoading}
                  >
                    {actionLoading && actionStockId === stockId ? 'Closing...' : 'Close Position'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StockChart;